import React from 'react';
import { Dropdown, Menu, Modal, notification } from 'antd';
import {
  CompactTable,
  defaultResponseHandler,
  openNotificationWithIcon,
  Page,
  PermissionGuard,
  useAssetParams,
  useUserinfo,
} from '@mss/mss-component-library';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { MoreVertical } from 'react-feather';
import { availableDIDDiagnostics, diagnosticTypeToLabel } from './Detail';
import { useQuery } from '@tanstack/react-query';
import Fetch from '../../../util/_fetch';
import Button from '../../../Button';
import Api from '../../../../asset/services/Api';
import { sanitizedUrl } from '../../../../asset/util/sanitize';

type AssetConfiguration = {
  enabled: boolean;
  diagnostic: string;
  did: string;
};

const ISAEFieldAssetOverview = () => {
  const { url } = useRouteMatch();
  const { assetId } = useAssetParams();
  const [t] = useTranslation(['asset', 'common', 'authorization']);
  const [hasPermission] = useUserinfo(assetId);

  const {
    data: configurations,
    isLoading,
    refetch: fetchConfigurations,
  } = useQuery({
    queryKey: ['configurations', assetId ?? ''],
    initialData: [],
    initialDataUpdatedAt: 1,
    enabled: !!assetId,
    queryFn: async () =>
      (
        await defaultResponseHandler<any[]>({
          t,
          request: Fetch.get(
            `/api/applications/assetmanagement/isae/configuration_list/?id=${assetId}`
          ),
        })
      ).data,
  });

  const deleteConfiguration = async (configId: string) =>
    Modal.confirm({
      title: t('Are you sure you want to delete?'),
      icon: (
        <span className="anticon">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            color="#fd9644"
            size="sm"
          />
        </span>
      ),
      content: (
        <div>
          <blockquote>{t('ISAE Config')}</blockquote>
          <p>{t("You're about to delete this configuration.")}</p>
        </div>
      ),
      onOk: async () => {
        const resp = await Api.assetmanagement.deleteConfig(configId);
        if (resp.status >= 200 && resp.status < 300) {
          fetchConfigurations();
          openNotificationWithIcon(
            notification.success,
            t(`Configuration deleted`),
            '',
            5
          );
        } else {
          openNotificationWithIcon(
            notification.error,
            t('Deleting configuration failed'),
            t('The configuration could not be deleted. Please try again later.')
          );
        }
      },
      okButtonProps: { danger: true },
      okText: t('OK'),
      cancelText: t('Cancel'),
    });

  const history = useHistory();
  return (
    <Page
      padding={false}
      actions={
        <PermissionGuard
          any
          codename={['change_assetmanagementapplicationisae']}
        >
          <Button
            onClick={() =>
              history.push(`${sanitizedUrl(url)}/configuration-settings`)
            }
            data-ta="newConfigurationButton"
            className="mb-1"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1" />
            {t('ADD Configuration')}
          </Button>
        </PermissionGuard>
      }
      main={
        <PermissionGuard any codename={['view_assetmanagementapplicationisae']}>
          <CompactTable
            bordered={false}
            dataSource={configurations}
            loading={isLoading}
            columns={columns({
              t,
              hasPermission,
              deleteConfiguration,
              configurations,
            })}
            rowKey={'id'}
            pagination={{ showQuickJumper: true, showSizeChanger: true }}
            onRow={(record: any) => {
              return {
                onClick: () =>
                  history.push(
                    `${sanitizedUrl(url)}/configuration-settings/${record.id}`
                  ),
              };
            }}
          />
        </PermissionGuard>
      }
    />
  );
};

const columns = ({
  t,
  hasPermission,
  deleteConfiguration,
  configurations,
}: {
  t: TFunction;
  hasPermission: any;
  deleteConfiguration: any;
  configurations: any;
}) => [
  {
    title: t('Device Tag'),
    dataIndex: 'device_tag',
    key: 'device_tag',
    width: 150,
    sorter: () => 0,
    filterMultiple: true,
    filters: configurations.reduce((acc: any[], item: any) => {
      if (acc.find(x => x.value === item.device_tag)) {
        return acc;
      }
      return [
        ...acc,
        {
          text: item.device_tag,
          value: item.device_tag,
        },
      ];
    }, []),
    onFilter: (value: any, record: any) => record.device_tag === value,
    filterSearch: true,
  },
  {
    title: t('Device ID'),
    dataIndex: 'device_id',
    key: 'device_id',
    width: 150,
    sorter: () => 0,
    filterMultiple: true,
    filters: configurations.reduce((acc: any[], item: any) => {
      if (acc.find(x => x.value === item.device_id)) {
        return acc;
      }
      return [
        ...acc,
        {
          text: item.device_id,
          value: item.device_id,
        },
      ];
    }, []),
    onFilter: (value: any, record: any) => record.device_id === value,
    filterSearch: true,
  },
  {
    title: t('Diagnostics Active'),
    dataIndex: 'asset_config',
    key: 'asset_config',
    sorter: () => 0,
    filterMultiple: true,
    filters: availableDIDDiagnostics.map(item => ({
      text: diagnosticTypeToLabel(item, t),
      value: item,
    })),
    onFilter: (value: any, record: any) => {
      return record.asset_config[value]?.enabled;
    },
    render: (x: AssetConfiguration[]) => (
      <>
        {Object.values(x).map(item =>
          item.enabled ? <div className="mt-1">{item.diagnostic}</div> : <></>
        )}
      </>
    ),
  },
  {
    title: '',
    key: 'action',
    width: 20,
    // @ts-ignore
    onCell: () => {
      return {
        onClick: (event: any) => {
          event.stopPropagation();
        },
      };
    },
    render: (_: any, record: any) =>
      hasPermission(['change_assetmanagementapplicationisae']) && (
        <Dropdown.Button
          // @ts-ignore
          type="text"
          icon={<MoreVertical size={14} />}
          overlay={
            <Menu>
              <Menu.Item
                hidden={!hasPermission('change_assetmanagementapplicationisae')}
                key="2"
                onClick={() => deleteConfiguration(record.id)}
              >
                {t('Delete')}
              </Menu.Item>
            </Menu>
          }
        />
      ),
  },
];

export default ISAEFieldAssetOverview;
